import React from 'react';
import { useTranslation } from 'react-i18next';
import { nanoid } from 'nanoid';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import { List, ListItem, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Stack from '@mui/material/Stack';
import Chip from '@material-ui/core/Chip';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import { Autocomplete } from '@material-ui/lab';

import ajaxGet from '../../services/ajaxGet.js';
import ajaxPost from '../../services/ajaxPost.js';
import { mainPimColor } from '../PimTogoTheme';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import FacetBox from './FacetBox';

const useStyles_ = makeStyles((theme) => ({
  root: {
    width: '100%',
    //   maxWidth: 360,
    backgroundColor: theme.palette.background.paper,
    position: 'relative',
    overflowY: 'auto',
    overflowX: 'hidden',
    fontSize: '2rem',
    paddingTop: '4rem',
    maxHeight: 300
  },
  listSection: {
    backgroundColor: 'inherit'
  },
  ul: {
    backgroundColor: 'inherit',
    padding: 0
  },
  column: {
    flexBasis: '33.33%'
  },
  heading: {
    fontSize: '1.5rem !important',
    fontWeight: '500 !important',
    color: 'white !important'
  },
  resetButton: {
    fontSize: '1.5rem !important',
    fontWeight: '500 !important',
    color: 'white !important',
    '&&:hover': {
      backgroundColor: ' rgb(0 0 0 / 15%)'
    }
  },
  facet: {
    fontSize: '1.2rem !important',
    fontWeight: 'bold !important'
  },
  facet_: {
    fontSize: '0.9rem !important'
    // fontWeight: 'bold !important'
  },
  listItemButtonStyle: {
    borderRight: '2px solid orange !important',
    '&.Mui-selected': {
      backgroundColor: 'unset !important',
      borderTop: '2px solid orange',
      borderBottom: '2px solid orange',
      borderRight: 'none !important'
    }
  },
  fontFix: {
    fontSize: '12px'
  },
  fontFixPaddingFix: {
    fontSize: '12px',
    paddingLeft: '16px !important'
  },
  fontFixPaddingFixBold: {
    fontSize: '12px',
    paddingLeft: '16px !important',
    fontWeight: 'bold !important'
  },
  facetSelectInput: {
    borderRight: '2px solid orange !important',
    '& .MuiInput-underline:before': {
      borderBottom: 'none !important'
    }
  },
  facetSelectInputSelected: {
    backgroundColor: 'unset !important',
    borderTop: '2px solid orange',
    borderBottom: '2px solid orange',
    borderRight: 'none !important',
    '& .MuiInput-underline:before': {
      borderBottom: 'none !important'
    }
  },
  facetInputLabel: {
    fontSize: '13px',
    paddingLeft: '16px !important'
  },
  resetIcon: {
    '&.MuiChip-icon': {
      color: 'white !important'
    },
    '&.MuiSvgIcon-root ': {
      color: 'white !important'
    }
  },
  filterChip: {
    marginLeft: '0.5rem',
    marginTop: '0.5rem',
    '&.MuiChip-deletableColorSecondary:focus': {
      backgroundColor: '#f29528 !important'
    }
  },
  disabledFilterChip: {
    marginLeft: '0.5rem',
    marginTop: '0.5rem',
    opacity: 0.5
  }
}));

const isFacetValueChecked = (checked, value) => {
  if (value == undefined) {
    return false;
  }
  if (checked.length != 0) {
    for (let i = 0; i < checked.length; i++) {
      if (
        checked[i]?.searchIds[0] === value?.searchIds[0] &&
        value?.valueAsString === checked[i]?.valueAsString
      ) {
        return true;
      }
    }
    return false;
  } else {
    return false;
  }
};

export default function FilterProduct({
  setCountFoundTotal,
  setMultiClassificationSubscribed,
  setArticleDataEdit,
  setPaginationstate,
  setClassificationGroups,
  setProductsViewList,
  facets,
  setFacets,
  productStructureFilter,
  contentLanguage
}) {
  const { t } = useTranslation();
  const classes = useStyles_();

  const [facetElement, setFacetElement] = React.useState({});
  const [allSearchIds, setAllSearchIds] = React.useState([]);
  const [checked, setChecked] = React.useState([]);
  const [filter, setFilter] = React.useState([]);
  const [autocompleteId, setAutocompleteId] = React.useState('AutocompleteId');

  const containeredFacets = {};
  const containerOrder = [];

  facets?.combinedFacets?.forEach((combinedFacet) => {
    const container = containeredFacets[combinedFacet.facetContainerLabel];
    if (container) {
      container.push(combinedFacet);
    } else {
      containeredFacets[combinedFacet.facetContainerLabel] = [combinedFacet];
      containerOrder.push(combinedFacet.facetContainerLabel);
    }
  });

  const selectFacet = (facet) => {
    if (facet == null) {
      setFacetElement({});
      setAutocompleteId('AutocompleteId' + nanoid(10));
      return;
    }

    if (facet.facetContainerId == 'fixedFacetFields') {
      setAutocompleteId('AutocompleteId' + nanoid(10));
    }

    setFacetElement(facet);
  };

  const openFilterClick = () => {
    ajaxGet('dataManagement/facets_', {
      contentLanguage: contentLanguage
    }).then((ajaxData) => {
      const response = ajaxData.response;
      if (!ajaxData.error && response) {
        setFacets(response.facets);
      }
    });
  };

  const [expanded, setExpanded] = React.useState(false);

  const handleChange = (panel) => (_, isExpanded) => {
    openFilterClick();
    setExpanded(isExpanded ? panel : false);
  };

  const updateSearch = (filter) => {
    const payload = {
      searchTerm: '*',
      curPage: null,
      elementsPerPage: null,
      filters: filter,
      isNewFilter: true
    };

    if (productStructureFilter && productStructureFilter.productStructureNode) {
      payload.productStructureNode =
        productStructureFilter.productStructureNode;
      payload.productStructureNodePath =
        productStructureFilter.productStructureNodePath;
    }

    ajaxPost('dataManagement_', {}, payload).then((ajaxData) => {
      let response = ajaxData.response;
      if (response)
        TOGO.Util.notifyResponse(response.message, !response.success);

      if (!ajaxData.error && response) {
        let productList = response.overviewModel.items;
        setProductsViewList([]);
        setCountFoundTotal(response.countFoundTotal);
        setArticleDataEdit(response.articleDataEdit);
        setPaginationstate(response.pagination);
        setClassificationGroups(response.groupModels);
        setFacets(response.facets);
        setMultiClassificationSubscribed(
          response.multiClassificationSubscribed
        );
        setProductsViewList(productList);

        setFacets(response.facets);
      }
    });
  };

  const resetFilterHandleClick = () => {
    setChecked([]);
    setAllSearchIds([]);
    setFilter([]);
    updateSearch([]);
  };

  const changeAllSearchIds = (item, bool) => {
    if (bool) {
      allSearchIds.push(item);
      setAllSearchIds(allSearchIds);
    } else {
      setAllSearchIds(allSearchIds.filter((org) => org !== item));
    }
  };

  // if (
  //   (!facets?.fixedFacets || facets?.fixedFacets.length == 0) &&
  //   (!facets?.combinedFacets || facets?.combinedFacets.length == 0)
  // ) {
  //   return null;
  // }

  const deleteFacetChip = (checkedFacetValue, isCheckedChipRelevant) => {
    const currentIndex = checked.findIndex(
      (item) =>
        item?.searchIds[0] == checkedFacetValue?.searchIds[0] &&
        checkedFacetValue?.valueAsString == item?.valueAsString
    );
    const newChecked = [...checked];

    if (currentIndex >= 0) {
      newChecked.splice(currentIndex, 1);
      deleteFilterValue(checkedFacetValue);
    }

    setChecked(newChecked);

    if (isCheckedChipRelevant) {
      selectFacetFromFacetValue(checkedFacetValue);
    }
  };

  const selectFacetChip = (checkedFacetValue) => {
    selectFacetFromFacetValue(checkedFacetValue);
  };

  const selectFacetFromFacetValue = (facetValue) => {
    let facetToSelect = null;

    if (!facetValue.searchIds?.length) {
      return;
    }

    if (facets?.fixedFacets) {
      facetToSelect = facets?.fixedFacets.find(
        (fixedFacet) => fixedFacet.searchId === facetValue.searchIds[0]
      );

      if (facetToSelect) {
        selectFacet(facetToSelect);
        return;
      }
    }

    if (facets?.combinedFacets) {
      facetToSelect = facets?.combinedFacets.find(
        (combinedFacet) =>
          combinedFacet.searchIds?.length &&
          combinedFacet.searchIds[0] === facetValue.searchIds[0]
      );
    }

    selectFacet(facetToSelect);
  };

  const checkIsCheckedChipRelevant = (checkedFacet) => {
    if (
      !facets.fixedFacets ||
      !facets.combinedFacets ||
      checkedFacet.searchIds.length == 0
    ) {
      return false;
    }

    facets.fixedFacets.find(
      (fixedFacet) => checkedFacet.searchIds[0] == fixedFacet.searchId
    );

    let isRelevant = false;
    if (checkedFacet.searchIds.length) {
      const targetFixedFacet = facets.fixedFacets.find(
        (fixedFacet) => checkedFacet.searchIds[0] == fixedFacet.searchId
      );
      if (targetFixedFacet?.valueGroups.length) {
        targetFixedFacet.valueGroups.forEach((valueGroup) => {
          if (isRelevant) {
            return;
          }
          if (
            valueGroup.values.find(
              (value) => value.valueAsString == checkedFacet.valueAsString
            )
          ) {
            isRelevant = true;
          }
        });
      }

      const targetCombinedFacet = facets.combinedFacets.find(
        (combinedFacet) =>
          combinedFacet.searchIds.length &&
          checkedFacet.searchIds[0] == combinedFacet.searchIds[0]
      );
      if (!isRelevant && targetCombinedFacet?.values.length) {
        if (
          targetCombinedFacet.values.find(
            (value) => value.valueAsString == checkedFacet.valueAsString
          )
        ) {
          isRelevant = true;
        }
      }
    }

    if (isRelevant) {
      return true;
    }

    return false;
  };

  const deleteFilterValue = (item) => {
    filter?.map((textSearchFilter, idx) => {
      if (textSearchFilter?.allSearchIds[0] === item?.searchIds[0]) {
        textSearchFilter.values.map((textSearch, index) => {
          if (textSearch.value == item?.valueAsString) {
            textSearchFilter.values.splice(index, 1);
            if (textSearchFilter.values.length == 0) {
              filter.splice(idx, 1);
              changeAllSearchIds(item?.searchIds[0], false);
            }
          }
        });
      }
    });

    setFilter(filter);
    updateSearch(filter);
  };

  return (
    <div>
      <Accordion
        expanded={expanded === 'panel1'}
        onChange={handleChange('panel1')}
        sx={{
          backgroundColor: mainPimColor
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon className={classes.resetIcon} />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <div className={classes.column}>
            <Typography className={classes.heading}>Filter</Typography>
          </div>
          {allSearchIds.length != 0 && (
            <div className={classes.column}>
              <Chip
                label={t('products.resetFilters')}
                className={classes.resetButton}
                variant="outlined"
                onClick={resetFilterHandleClick}
                clickable={true}
                icon={<AutorenewIcon className={classes.resetIcon} />}
              />
            </div>
          )}
        </AccordionSummary>
        <AccordionDetails>
          <Stack
            direction={{ xs: 'column', sm: 'column' }}
            style={{ width: '100%' }}
          >
            <Box sx={{ background: 'white' }}>
              {checked.map((checkedFacet) => {
                if (checkIsCheckedChipRelevant(checkedFacet)) {
                  return (
                    <Chip
                      key={
                        checkedFacet.searchIds[0] + checkedFacet.valueAsString
                      }
                      className={classes.filterChip}
                      label={
                        checkedFacet.facetLabel + ': ' + checkedFacet.valueLabel
                      }
                      onDelete={() => {
                        deleteFacetChip(checkedFacet, true);
                      }}
                      onClick={() => {
                        selectFacetChip(checkedFacet);
                      }}
                      color="secondary"
                    />
                  );
                }
                return (
                  <Chip
                    key={checkedFacet.searchIds[0] + checkedFacet.valueAsString}
                    className={classes.disabledFilterChip}
                    label={
                      checkedFacet.facetLabel + ': ' + checkedFacet.valueLabel
                    }
                    onDelete={() => {
                      deleteFacetChip(checkedFacet, false);
                    }}
                    color="secondary"
                  />
                );
              })}
            </Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 0.5, sm: 0.5, md: 0.5 }}
              sx={{
                width: '100%',
                height: '30rem',
                fontSize: '1.5rem',
                backgroundColor: '#fff'
              }}
            >
              <Box
                sx={{
                  width: '37.5%',
                  height: '30rem',
                  backgroundColor: '#fff',
                  fontSize: '1.5rem'
                }}
              >
                <List
                  className={classes.root}
                  component="nav"
                  aria-label="secondary mailbox folder"
                >
                  <ListItem
                    dense={true}
                    divider={true}
                    className={classes.listItemButtonStyle}
                  ></ListItem>
                  {facets?.fixedFacets &&
                    facets?.fixedFacets.map((fixedFacet) => (
                      <ListItemButton
                        key={fixedFacet.facetIdentifier}
                        className={classes.listItemButtonStyle}
                        selected={
                          facetElement != null &&
                          fixedFacet.facetIdentifier ===
                            facetElement.facetIdentifier
                        }
                        onClick={() => {
                          selectFacet(fixedFacet);
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography
                              fontSize={12}
                              className={
                                allSearchIds.indexOf(
                                  fixedFacet.facetIdentifier
                                ) != -1
                                  ? classes.facet
                                  : ''
                              }
                            >
                              {fixedFacet.label}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    ))}
                  {facets?.combinedFacets && (
                    <Autocomplete
                      key={autocompleteId}
                      size="medium"
                      id="grouped-facets-autocomplete"
                      className={
                        facetElement?.facetBoxId == undefined
                          ? classes.facetSelectInput
                          : classes.facetSelectInputSelected
                      }
                      classes={
                        facetElement?.searchIds &&
                        allSearchIds.indexOf(facetElement.searchIds[0]) != -1
                          ? { input: classes.fontFixPaddingFixBold }
                          : { input: classes.fontFixPaddingFix }
                      }
                      options={facets.combinedFacets.sort(
                        (a, b) =>
                          containerOrder.indexOf(a.facetContainerLabel) -
                          containerOrder.indexOf(b.facetContainerLabel)
                      )}
                      groupBy={(combinedFacet) => {
                        const label =
                          combinedFacet.facetContainerLabel == 'customFields'
                            ? t('data.customFields')
                            : combinedFacet.facetContainerLabel;
                        return label;
                      }}
                      getOptionLabel={(option) =>
                        option.label ? option.label : ''
                      }
                      renderOption={(option) => {
                        if (
                          option.searchIds &&
                          allSearchIds.indexOf(option.searchIds[0]) != -1
                        ) {
                          return (
                            <span style={{ fontWeight: 'bold' }}>
                              {option.label}
                            </span>
                          );
                        }

                        return <span>{option.label}</span>;
                      }}
                      noOptionsText={t('common.noResults')}
                      value={
                        facetElement?.facetContainerId == 'fixedFacetFields' ||
                        !facetElement.facetBoxId
                          ? null
                          : facetElement
                      }
                      getOptionSelected={(option, value) =>
                        option.facetBoxId === value.facetBoxId
                      }
                      onFocus={() => {
                        if (
                          facetElement != null &&
                          facetElement.facetContainerId == 'fixedFacetFields'
                        ) {
                          setFacetElement({});
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('products.facetSelectorFeaturesFields')}
                          color="secondary"
                          InputLabelProps={{
                            className: classes.facetInputLabel
                          }}
                          style={{ color: 'green' }}
                          variant="standard"
                        />
                      )}
                      clearOnBlur={true}
                      onChange={(_, combinedFacet) => {
                        selectFacet(combinedFacet);
                      }}
                    />
                  )}
                  <ListItem
                    dense={true}
                    divider={true}
                    className={classes.listItemButtonStyle}
                  ></ListItem>
                </List>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  height: '30rem',
                  backgroundColor: '#fff'
                }}
              >
                <FacetBox
                  filter={filter}
                  setFilter={setFilter}
                  facetElement={facetElement}
                  allSearchIds={allSearchIds}
                  changeAllSearchIds={changeAllSearchIds}
                  updateSearch={updateSearch}
                  deleteFilterValue={deleteFilterValue}
                  checked={checked}
                  setChecked={setChecked}
                  isFacetValueChecked={isFacetValueChecked}
                ></FacetBox>
              </Box>
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </div>
  );
}
